import { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    RadioGroup,
    FormControlLabel,
    Radio,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    FormGroup,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import axiosConfig from '../../util/axiosConfig';
import { API_ENDPOINTS } from '../../util/apiEndpoints';
import { useStationsContext } from '../../context/StationsContext';

const PARAM_GROUPS = {
    Air: [
        {
            category: 'Temperature (°C)',
            params: [
                { id: 'at_2m_deg c', label: '2m' },
                { id: 'at_4m_deg c', label: '4m' },
                { id: 'at_6m_deg c', label: '6m' },
            ],
        },
        {
            category: 'Relative Humidity (%)',
            params: [
                { id: 'rh_2m_%', label: '2m' },
                { id: 'rh_4m_%', label: '4m' },
                { id: 'rh_6m_%', label: '6m' },
            ],
        },
        {
            category: 'Wind Speed (m/s)',
            params: [
                { id: 'ws_2m_mps', label: '2m' },
                { id: 'ws_4m_mps', label: '4m' },
                { id: 'ws_6m_mps', label: '6m' },
            ],
        },
        {
            category: 'Wind Direction (°)',
            params: [
                { id: 'wd_2m_deg', label: '2m' },
                { id: 'wd_4m_deg', label: '4m' },
                { id: 'wd_6m_deg', label: '6m' },
            ],
        },
        {
            category: 'Radiation',
            params: [
                { id: 'sr_2m_w/m2', label: 'Solar Radiation (W/m²)' },
                { id: 'net radiation_w/m2', label: 'Net Radiation (W/m²)' },
            ],
        },
        {
            category: 'Rainfall',
            params: [
                { id: 'eventwise rf', label: 'Eventwise Rainfall' },
                { id: 'actual rf', label: 'Actual Rainfall' }
            ],
        },
    ],
    Soil: [
        {
            depth: '15 cm',
            params: [
                { id: 'sm_15cm_vol%', label: 'Moisture (%)' },
                { id: 'st_15cm_deg c', label: 'Temp (°C)' },
                { id: 'ec_15cm_ds/m', label: 'EC (dS/m)' }
            ]
        },
        {
            depth: '30 cm',
            params: [
                { id: 'sm_30cm_vol%', label: 'Moisture (%)' },
                { id: 'st_30cm_deg c', label: 'Temp (°C)' },
                { id: 'ec_30cm_ds/m', label: 'EC (dS/m)' }
            ]
        },
        {
            depth: '45 cm',
            params: [
                { id: 'sm_45cm_vol%', label: 'Moisture (%)' },
                { id: 'st_45cm_deg c', label: 'Temp (°C)' },
                { id: 'ec_45cm_ds/m', label: 'EC (dS/m)' }
            ]
        },
        {
            depth: '55 cm',
            params: [
                { id: 'sm_55cm_vol%', label: 'Moisture (%)' },
                { id: 'st_55cm_deg c', label: 'Temp (°C)' },
                { id: 'ec_55cm_ds/m', label: 'EC (dS/m)' }
            ]
        },
        {
            depth: '60 cm',
            params: [
                { id: 'sm_60cm_vol%', label: 'Moisture (%)' },
                { id: 'st_60cm_deg c', label: 'Temp (°C)' },
                { id: 'ec_60cm_ds/m', label: 'EC (dS/m)' }
            ]
        },
        {
            depth: '80 cm',
            params: [
                { id: 'sm_80cm_vol%', label: 'Moisture (%)' },
                { id: 'st_80cm_deg c', label: 'Temp (°C)' },
                { id: 'ec_80cm_ds/m', label: 'EC (dS/m)' }
            ]
        },
        {
            depth: '90 cm',
            params: [
                { id: 'sm_90cm_vol%', label: 'Moisture (%)' },
                { id: 'st_90cm_deg c', label: 'Temp (°C)' },
                { id: 'ec_90cm_ds/m', label: 'EC (dS/m)' }
            ]
        },
        {
            depth: '120 cm',
            params: [
                { id: 'sm_120cm_vol%', label: 'Moisture (%)' },
                { id: 'st_120cm_deg c', label: 'Temp (°C)' },
                { id: 'ec_120cm_ds/m', label: 'EC (dS/m)' }
            ]
        }
    ],
    Hourly: [
        {
            category: "Temperature (Hourly)",
            params: [
                { id: "at_2m_hr", label: "2m Temp (Hr)" },
                { id: "at_4m_hr", label: "4m Temp (Hr)" },
                { id: "at_6m_hr", label: "6m Temp (Hr)" },
            ],
        },
        {
            category: "Relative Humidity (Hourly)",
            params: [
                { id: "rh_2m_hr", label: "2m RH (Hr)" },
                { id: "rh_4m_hr", label: "4m RH (Hr)" },
                { id: "rh_6m_hr", label: "6m RH (Hr)" },
            ],
        },
        {
            category: "Wind Speed (Hourly)",
            params: [
                { id: "ws_2m_hr", label: "2m WS (Hr)" },
                { id: "ws_4m_hr", label: "4m WS (Hr)" },
                { id: "ws_6m_hr", label: "6m WS (Hr)" },
            ],
        },
        {
            category: "Air Pressure (Hourly)",
            params: [
                { id: "ap_2m_h_pa_hr", label: "AP 2m hPa (Hr)" },
            ],
        }
    ],
};

// 🔹 All-param lists for top-level Select All / Deselect All
const ALL_AIR_PARAMS = PARAM_GROUPS.Air.flatMap(group =>
    group.params.map(p => p.id)
);

const ALL_SOIL_PARAMS = PARAM_GROUPS.Soil.flatMap(group =>
    group.params.map(p => p.id)
);

const ALL_HOURLY_PARAMS = PARAM_GROUPS.Hourly.flatMap(group =>
    group.params.map(p => p.id)
);

const DownloadData = () => {
    // State
    const [stations, setStations] = useState([]);
    const { stations: ctxStations, getStationName } = useStationsContext();

    const [dateMode, setDateMode] = useState('range'); // 'single' | 'range'
    const [singleDate, setSingleDate] = useState(dayjs());
    const [fromDate, setFromDate] = useState(dayjs().subtract(7, 'day'));
    const [toDate, setToDate] = useState(dayjs());

    const [selectedParams, setSelectedParams] = useState(new Set());
    const [frequency, setFrequency] = useState('daily');
    const [format, setFormat] = useState('csv');

    const [previewData, setPreviewData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);



    // Handlers
    const handleStationChange = (event) => {
        const {
            target: { value },
        } = event;
        setStations(typeof value === 'string' ? value.split(',') : value);
    };

    const handleParamToggle = (id) => {
        const newParams = new Set(selectedParams);
        if (newParams.has(id)) {
            newParams.delete(id);
        } else {
            newParams.add(id);
        }
        setSelectedParams(newParams);
    };

    const selectAllParams = (paramIds) => {
        setSelectedParams((prev) => {
            const newSet = new Set(prev);
            paramIds.forEach((id) => newSet.add(id));
            return newSet;
        });
    };

    const deselectAllParams = (paramIds) => {
        setSelectedParams((prev) => {
            const newSet = new Set(prev);
            paramIds.forEach((id) => newSet.delete(id));
            return newSet;
        });
    };

    const handlePreview = async () => {
        setLoading(true);
        setError(null);
        setPreviewData([]);

        if (stations.length === 0) {
            setError("Please select at least one station.");
            setLoading(false);
            return;
        }
        if (selectedParams.size === 0) {
            setError("Please select at least one parameter.");
            setLoading(false);
            return;
        }

        const queryParams = {
            stations: stations.join(","),
            params: Array.from(selectedParams).join(","),
            frequency, // no useHourly logic now
        };

        if (dateMode === "single") {
            const start = singleDate.startOf("day");      // 00:00:00
            const end = singleDate.endOf("day");          // 23:59:59
            queryParams.from = start.format("YYYY-MM-DD HH:mm:ss");
            queryParams.to = end.format("YYYY-MM-DD HH:mm:ss");
        } else {
            queryParams.from = fromDate.format("YYYY-MM-DD HH:mm:ss");
            queryParams.to = toDate.format("YYYY-MM-DD HH:mm:ss");
        }

        console.log("🔍 Preview queryParams:", queryParams);

        try {
            const res = await axiosConfig.get(API_ENDPOINTS.PREVIEW_REPORT, {
                params: queryParams,
            });

            console.log("✅ Preview response:", res.data);

            if (Array.isArray(res.data)) {
                setPreviewData(res.data);
            } else if (Array.isArray(res.data?.data)) {
                setPreviewData(res.data.data);
            } else {
                setError("Unexpected response format from server");
            }
        } catch (err) {
            console.error("❌ Preview error:", err);
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to load preview data";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const resetFilters = () => {
        setStations([]);
        setDateMode("range");
        setSingleDate(dayjs());
        setFromDate(dayjs().subtract(7, "day"));
        setToDate(dayjs());
        setSelectedParams(new Set());
        setFrequency("raw");
        setFormat("csv");
        setPreviewData([]);
        setError(null);
    };
    const handleDownload = async () => {
        if (stations.length === 0 || selectedParams.size === 0) return;

        try {
            const queryParams = {
                stations: stations.join(","),
                params: Array.from(selectedParams).join(","),
                frequency,
                format,
            };

            if (dateMode === "single") {
                const start = singleDate.startOf("day");
                const end = singleDate.endOf("day");
                queryParams.from = start.format("YYYY-MM-DD HH:mm:ss");
                queryParams.to = end.format("YYYY-MM-DD HH:mm:ss");
            } else {
                queryParams.from = fromDate.format("YYYY-MM-DD HH:mm:ss");
                queryParams.to = toDate.format("YYYY-MM-DD HH:mm:ss");
            }

            // 🔹 Call Spring Boot download endpoint as BLOB
            const res = await axiosConfig.get(API_ENDPOINTS.DOWNLOAD_REPORT, {
                params: queryParams,
                responseType: "blob",
            });

            // 🔹 Try to get filename from Content-Disposition, else fallback
            const disposition = res.headers["content-disposition"];
            let filename = "report";

            if (disposition && disposition.includes("filename=")) {
                const match = disposition.match(/filename="?([^"]+)"?/);
                if (match && match[1]) {
                    filename = match[1];
                }
            } else {
                // fallback based on format
                filename = format === "json" ? "report.json" : "report.csv";
            }

            // 🔹 Create download link
            const blob = new Blob([res.data], { type: res.headers["content-type"] });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            // 🔹 After successful download, reset filters
            resetFilters();
        } catch (err) {
            console.error("❌ Download error:", err);
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to download report";
            setError(msg);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4, maxWidth: "100% !important", overflowX: "hidden" }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h1" sx={{ fontSize: '2.1rem', mb: 1, color: 'primary.main' }}>
                    Download Report
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Select station, parameters, and time range to download data.
                </Typography>
            </Box>

            <Grid container spacing={4}>
                {/* Filters Column */}
                <Grid
                    item
                    xs={12}
                    md={4}
                    lg={3}
                    sx={{
                        height: "100vh",
                        overflow: "hidden"
                    }}>
                    <Paper sx={{
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        borderRadius: 0.5,


                        height: "100%",
                        overflowY: "auto",
                        scrollbarWidth: "thin"
                    }}>



                        {/* 1. Station Selection */}
                        <Box>
                            <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "orange", mb: 1 }}>1. Station</Typography>
                            <Typography variant="h6" gutterBottom></Typography>
                            <FormControl fullWidth>
                                <InputLabel id="station-label">Select Stations</InputLabel>
                                <Select
                                    labelId="station-label"
                                    label="Select Stations"
                                    multiple
                                    value={stations}
                                    onChange={handleStationChange}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip
                                                    key={value}
                                                    label={getStationName ? getStationName(value) : value}
                                                    size="small"

                                                    onDelete={(e) => {
                                                        e.stopPropagation();
                                                        setStations(stations.filter(v => v !== value));
                                                    }}

                                                    onMouseDown={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    sx={{ "& .MuiChip-deleteIcon": { color: "#d32f2f", "&:hover": { color: "#b71c1c" } } }} />
                                            ))}
                                        </Box>)}>
                                    {ctxStations && ctxStations.length > 0 ? (
                                        ctxStations.map((name) => (
                                            <MenuItem key={name} value={name}>
                                                <Checkbox checked={stations.indexOf(name) > -1} />
                                                <ListItemText primary={getStationName ? getStationName(name) : name} />
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>
                                            <ListItemText primary="No stations available" />
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                        <Divider />


                        {/* 2. Date & Time */}
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "20px",
                                    fontWeight: 700,
                                    color: "orange",
                                    mb: 1,
                                }}
                            >
                                2. Date & Range
                            </Typography>

                            <RadioGroup
                                row
                                value={dateMode}
                                onChange={(e) => setDateMode(e.target.value)}
                                sx={{ mb: 2 }}
                            >
                                <FormControlLabel value="single" control={<Radio />} label="Single" />
                                <FormControlLabel value="range" control={<Radio />} label="Range" />
                            </RadioGroup>

                            {(() => {
                                const PICKER_STYLE = {
                                    "& .MuiPickersPopper-root": {
                                        zIndex: "99999 !important",
                                    },

                                    "& .MuiPaper-root": {
                                        minWidth: "380px",
                                    },

                                    "& .MuiMenuItem-root.Mui-selected": {
                                        borderRadius: "100px !important",
                                        padding: "1px 1px",
                                        backgroundColor: "primary!important",
                                        color: "#fff !important",

                                    },

                                    "& .MuiMenuItem-root": {
                                        borderRadius: "18px",
                                        margin: "2px 6px",
                                    },
                                    "& .MuiMultiSectionDigitalClockSection-root": {
                                        minWidth: "70px !important",
                                    },


                                    "& .MuiMultiSectionDigitalClockSection-root:last-of-type": {
                                        minWidth: "70px",
                                    },


                                    "& .MuiDialogActions-root button": {
                                        background: "#ff8c00",
                                        color: "white",
                                        borderRadius: "40px",
                                        padding: "5px 30px",
                                    },
                                };

                                const commonProps = {
                                    closeOnSelect: false,
                                    slotProps: {
                                        popper: {
                                            sx: PICKER_STYLE,
                                            container: document.body,
                                            disablePortal: true,
                                        },
                                    },
                                };

                                return (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        {dateMode === "single" ? (
                                            <DateTimePicker
                                                label="Select Date-Time"
                                                value={singleDate}
                                                onChange={(newValue) => setSingleDate(newValue)}
                                                {...commonProps}
                                            />
                                        ) : (
                                            <>
                                                <DateTimePicker
                                                    label="From"
                                                    value={fromDate}
                                                    onChange={(newValue) => setFromDate(newValue)}
                                                    {...commonProps}
                                                />
                                                <DateTimePicker
                                                    label="To"
                                                    value={toDate}
                                                    onChange={(newValue) => setToDate(newValue)}
                                                    {...commonProps}
                                                />
                                            </>
                                        )}
                                    </Box>
                                );
                            })()}
                        </Box>

                        <Divider />





                        {/* 3. Parameters */}
                        <Box>
                            <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "orange", mb: 0 }}>3. Parameters</Typography>



                            {/* Hourly Group as Accordion */}
                            <Accordion
                                disableGutters
                                elevation={0}
                                sx={{
                                    "&:before": { display: "none" },
                                    width: "100%",
                                    maxWidth: "100%"
                                }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        alignItems: "flex-start !important",

                                        "& .MuiAccordionSummary-content": {
                                            margin: "0 !important",
                                            alignItems: "flex-start"
                                        },

                                        "& .MuiAccordionSummary-expandIconWrapper": {
                                            marginTop: "4px",
                                            color: "orange"
                                        }
                                    }} >

                                    <Box sx={{ width: "100%" }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                width: "100%"
                                                //position: "relative"
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "17px",
                                                    fontWeight: 600,
                                                    color: "primary.main",
                                                    textAlign: "center",
                                                    mb: 0,
                                                    lineHeight: 1
                                                }}
                                            >
                                                Hourly (Aggregated)
                                            </Typography>
                                        </Box>
                                        <Box
                                        sx={{
                                            display: "flex",
                                            gap: 2,
                                            mt: "2px",
                                            whiteSpace: "nowrap",
                                            justifyContent: "center"
                                        }}
                                        >
                                            <Button
                                                component="span"
                                                size="small"
                                                variant="text"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    selectAllParams(ALL_HOURLY_PARAMS);
                                                }}
                                            >
                                                Select All
                                            </Button>

                                            <Button
                                                component="span"
                                                size="small"
                                                variant="text"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deselectAllParams(ALL_HOURLY_PARAMS);
                                                }}>
                                                Deselect All
                                            </Button>
                                        </Box>
                                    </Box>
                                </AccordionSummary>

                                <AccordionDetails>
                                    {PARAM_GROUPS.Hourly.map((group) => (
                                        <Box key={group.category} sx={{ mb: 2 }}>

                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {group.category}
                                            </Typography>

                                            <FormGroup row>
                                                {group.params.map((p) => (
                                                    <FormControlLabel
                                                        key={p.id}
                                                        control={
                                                            <Checkbox
                                                                checked={selectedParams.has(p.id)}
                                                                onChange={() => handleParamToggle(p.id)}
                                                                size="small"
                                                            />
                                                        }
                                                        label={p.label}
                                                        sx={{ mr: 2 }}
                                                    />
                                                ))}
                                            </FormGroup>

                                        </Box>
                                    ))}
                                </AccordionDetails>
                            </Accordion>



                            {/* Air Group */}
                            <Accordion
                                disableGutters
                                elevation={0}
                                sx={{
                                    "&:before": { display: "none" },
                                    width: "100%",
                                    maxWidth: "100%"
                                }}
                            >

                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        alignItems: "flex-start !important",

                                        "& .MuiAccordionSummary-content": {
                                            margin: "0 !important",
                                            alignItems: "flex-start"
                                        },

                                        "& .MuiAccordionSummary-expandIconWrapper": {
                                            marginTop: "4px",
                                            color: "orange"
                                        }
                                    }} >

                                    <Box sx={{ width: "100%" }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                width: "100%"
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "17px",
                                                    fontWeight: 600,
                                                    color: "primary.main",
                                                    textAlign: "center",
                                                    mb: 0,
                                                    lineHeight: 1,
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                Air (Above Ground)
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 2,
                                                mt: "2px",
                                                whiteSpace: "nowrap",
                                                justifyContent: "center"
                                            }}
                                        >
                                            <Button
                                                component="span"
                                                size="small"
                                                variant="text"
                                                sx={{ whiteSpace: "nowrap" }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    selectAllParams(ALL_AIR_PARAMS);
                                                }}
                                            >
                                                Select All
                                            </Button>

                                            <Button
                                                component="span"
                                                size="small"
                                                variant="text"
                                                sx={{ whiteSpace: "nowrap" }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deselectAllParams(ALL_AIR_PARAMS);
                                                }}
                                            >
                                                Deselect All
                                            </Button>
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {PARAM_GROUPS.Air.map((group) => (
                                        <Box key={group.category} sx={{ mb: 1.5 }}>

                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'text.secondary',
                                                    display: 'block',
                                                    mb: 0.5,
                                                }}
                                            >
                                                {group.category}
                                            </Typography>

                                            <FormGroup row>
                                                {group.params.map((p) => (
                                                    <FormControlLabel
                                                        key={p.id}
                                                        control={
                                                            <Checkbox
                                                                checked={selectedParams.has(p.id)}
                                                                onChange={() => handleParamToggle(p.id)}
                                                                size="small"
                                                            />
                                                        }
                                                        label={<Typography variant="body2">{p.label}</Typography>}
                                                        sx={{ mr: 2 }}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </Box>
                                    ))}
                                </AccordionDetails>
                            </Accordion>



                            {/* Soil Group */}

                            <Accordion
                                disableGutters
                                elevation={0}
                                sx={{
                                    "&:before": { display: "none" },
                                    width: "100%",
                                    maxWidth: "100%"
                                }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        alignItems: "flex-start !important",

                                        "& .MuiAccordionSummary-content": {
                                            margin: "0 !important",
                                            alignItems: "flex-start"
                                        },

                                        "& .MuiAccordionSummary-expandIconWrapper": {
                                            marginTop: "4px",
                                            color: "orange"
                                        }
                                    }} >

                                    <Box sx={{ width: "100%" }}>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                width: "100%"
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "17px",
                                                    fontWeight: 600,
                                                    color: "primary.main",
                                                    textAlign: "center",
                                                    lineHeight: 1
                                                }}
                                            >
                                                Soil (Depth-wise)
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                gap: 2,
                                                mt: "2px",
                                                whiteSpace: "nowrap",
                                                flexWrap: "nowrap"
                                            }}
                                        >
                                            <Button
                                                component="span"
                                                size="small"
                                                variant="text"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    selectAllParams(ALL_SOIL_PARAMS);
                                                }}
                                            >
                                                Select All
                                            </Button>

                                            <Button
                                                component="span"
                                                size="small"
                                                variant="text"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deselectAllParams(ALL_SOIL_PARAMS);
                                                }}
                                            >
                                                Deselect All
                                            </Button>
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {PARAM_GROUPS.Soil.map((depthGroup) => (
                                        <Box key={depthGroup.depth} sx={{ mb: 1 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'text.secondary',
                                                    display: 'block',
                                                    mb: 0.5
                                                }}
                                            >
                                                {depthGroup.depth}
                                            </Typography>
                                            <FormGroup row>
                                                {depthGroup.params.map(p => (
                                                    <FormControlLabel
                                                        key={p.id}
                                                        control={
                                                            <Checkbox
                                                                checked={selectedParams.has(p.id)}
                                                                onChange={() => handleParamToggle(p.id)}
                                                                size="small"
                                                            />
                                                        }
                                                        label={<Typography variant="body2">{p.label}</Typography>}
                                                        sx={{ mr: 2 }}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </Box>
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        </Box>




                        {/* 4. Frequency & 5. Format */}

                        <Box>
                            <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "orange", mb: 0, }}>4. Options</Typography>
                            <Typography variant="h6" gutterBottom></Typography>
                            <Typography sx={{ mt: 1, fontSize: "17px", fontWeight: 600, color: "primary.main" }}>Data Frequency</Typography>
                            <RadioGroup
                                row
                                value={frequency}
                                onChange={(e) => setFrequency(e.target.value)}
                                sx={{ display: "flex", flexWrap: "nowrap", gap: 1, }}>
                                <FormControlLabel value="raw" control={<Radio size="small" />} label="Raw" />
                                <FormControlLabel value="hourly" control={<Radio size="small" />} label="Hourly" />
                                <FormControlLabel value="daily" control={<Radio size="small" />} label="Daily" />
                            </RadioGroup>

                            <Typography sx={{ mt: 2, fontSize: "17px", fontWeight: 600, color: "primary.main" }}>Output Format</Typography>
                            <RadioGroup
                                row
                                value={format}
                                onChange={(e) => setFormat(e.target.value)}
                                sx={{ display: "flex", flexWrap: "nowrap", gap: 1, }}>
                                <FormControlLabel value="csv" control={<Radio size="small" />} label="CSV" />
                                <FormControlLabel value="xlsx" control={<Radio size="small" />} label="Excel" />
                                <FormControlLabel value="json" control={<Radio size="small" />} label="JSON" />
                            </RadioGroup>
                        </Box>

                    </Paper>
                </Grid>

                {/* Preview Column */}

                <Grid item xs={12} md={8} lg={9}>
                    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: "orange", fontSize: "25px" }}>Data Preview</Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={handlePreview}
                                    disabled={loading || stations.length === 0 || selectedParams.size === 0}
                                >
                                    Preview
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownload}
                                    disabled={loading || stations.length === 0 || selectedParams.size === 0}
                                >
                                    Download
                                </Button>
                            </Box>
                        </Box>

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                            * Preview shows limited rows (Max 50). Full download may contain up to 50,000 rows.
                        </Typography>

                        <TableContainer sx={{ flexGrow: 1, border: "1px solid", borderColor: "divider", borderRadius: 0.9, maxWidth: "100%", overflowX: "auto" }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ bgcolor: 'background.default', fontWeight: 600 }}>Time</TableCell>
                                        <TableCell sx={{ bgcolor: 'background.default', fontWeight: 600 }}>Station</TableCell>
                                        {Array.from(selectedParams).map(id => (
                                            <TableCell
                                                key={id}
                                                sx={{ bgcolor: 'background.default', fontWeight: 600, whiteSpace: 'nowrap' }}
                                            >
                                                {id}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {previewData.length > 0 ? (
                                        previewData.map((row, idx) => (
                                            <TableRow key={idx} hover>
                                                <TableCell>{row.time}</TableCell>
                                                <TableCell>{row.station_name}</TableCell>
                                                {Array.from(selectedParams).map(id => (
                                                    <TableCell key={id}>{row[id]}</TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={2 + selectedParams.size} align="center" sx={{ py: 8 }}>
                                                <Typography color="text.secondary">
                                                    {loading ? 'Loading...' : 'Select filters and click Preview to see data.'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default DownloadData;
